import React from "react";
import { Box, Typography, Button, Sheet, Stack } from "@mui/joy";
import { FileDown, Send, Building2, RotateCw } from "lucide-react";
import SideModal from "../SideModal";

interface IContractPreview {
    open: boolean;
    onClose: () => void;
    data: any;
    onGenerate?: (overrides: any) => void;
    isGenerating?: boolean;
    onSend?: () => void;
    isSending?: boolean;
    isSendDisabled?: boolean;
}

const ADDITIONAL_INFO_ITEMS = [
    "Roofing debris removal fee included",
    "Property to be cleaned of all project-related debris with magnet",
    "Clean gutters of all project-related roof debris",
    "Manufacturer Standard Warranty included",
    "Company 5-year workmanship warranty included",
    "Building Permit: Required - Billed at Final Invoice",
];

export default function ContractPreview({
    open,
    onClose,
    data,
    onGenerate,
    isGenerating,
    onSend,
    isSending,
    isSendDisabled,
}: IContractPreview) {
    const [localData, setLocalData] = React.useState(data);

    React.useEffect(() => {
        setLocalData(data);
    }, [data]);

    const COLOR_HEADER = "#1D3557";
    const COLOR_INSURANCE = "#FFFDEC";

    const display = (value?: string) => (value && value !== "N/A" ? value : "—");

    const GridCell = ({
        label,
        value,
        flex = 1,
        multiline = false,
    }: {
        label: string;
        value?: string;
        flex?: number | string;
        multiline?: boolean;
    }) => (
        <Box sx={{ flex, p: 1, border: "0.2px solid", borderColor: "neutral.200", bgcolor: "white", minWidth: 0 }}>
            <Typography
                sx={{ fontSize: "8px", fontWeight: 700, color: "neutral.500", textTransform: "uppercase", mb: 0.2 }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "neutral.800",
                    whiteSpace: multiline ? "pre-line" : "normal",
                    overflow: "visible",
                    wordBreak: "break-word",
                }}
            >
                {display(value)}
            </Typography>
        </Box>
    );

    const SectionHeader = ({ num, title }: { num: string; title: string }) => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: COLOR_HEADER,
                px: 1.5,
                py: 0.5,
                mt: 2,
            }}
        >
            <Typography sx={{ fontWeight: 800, fontSize: "12px", mr: 1.5, opacity: 0.8, color: "white" }}>
                {num}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "11px", letterSpacing: "0.05em", color: "white" }}>
                {title}
            </Typography>
        </Box>
    );

    const scope = localData?.RoofingScope;
    const vents = localData?.NailingVentilation;
    const gutters = localData?.GutterScope;
    const pricing = localData?.Pricing;

    return (
        <SideModal
            showAdditionalHeadersIcons={false}
            open={open}
            onClose={onClose}
            portalBoxProps={{ width: 850 }}
            title="Document Preview"
            description="Service agreement preview."
        >
            <Box sx={{ bgcolor: "background.level1", minHeight: "100%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: 0, my: 3, flex: 1, display: "flex", justifyContent: "center", mb: 3, width: "100%" }}>
                    <Sheet
                        sx={{
                            width: "100%",
                            maxWidth: "800px",
                            bgcolor: "white",
                            p: 5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            fontFamily: "'Albert Sans', sans-serif",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: COLOR_HEADER,
                                        borderRadius: "xs",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Building2 color="white" size={20} />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{ color: COLOR_HEADER, fontWeight: 900, fontSize: "14px", lineHeight: 1 }}
                                    >
                                        1ST COLORADO
                                    </Typography>
                                    <Typography sx={{ color: COLOR_HEADER, fontWeight: 500, fontSize: "12px" }}>
                                        ROOFING
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                                <Typography sx={{ fontWeight: 900, fontSize: "18px", color: COLOR_HEADER }}>
                                    SERVICE AGREEMENT
                                </Typography>
                                <Typography sx={{ fontSize: "10px", color: "neutral.500", fontWeight: 600 }}>
                                    REF: {localData?.Header?.ReferenceNumber} | {localData?.Header?.Date}
                                </Typography>
                            </Box>
                        </Box>

                        <SectionHeader num="1" title="OWNER INFORMATION" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="OWNER NAME" value={localData?.OwnerInfo?.Name} />
                            <GridCell label="SALES REP" value={localData?.OwnerInfo?.SalesRep} />
                            <GridCell label="DATE" value={localData?.OwnerInfo?.Date} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="JOB ADDRESS" value={localData?.OwnerInfo?.Address} flex={2} />
                            <GridCell label="CLAIM NUMBER" value={localData?.OwnerInfo?.ClaimNumber} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="CITY" value={localData?.OwnerInfo?.City} />
                            <GridCell label="STATE" value={localData?.OwnerInfo?.State} />
                            <GridCell label="ZIP / COUNTY" value={localData?.OwnerInfo?.Zip} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PHONE" value={localData?.OwnerInfo?.Phone} />
                            <GridCell label="EMAIL" value={localData?.OwnerInfo?.Email} />
                            <GridCell label="INSURANCE COMPANY" value={localData?.OwnerInfo?.InsuranceCompany} />
                        </Stack>

                        <SectionHeader num="2" title="ROOFING - MATERIAL & UNDERLAYMENT" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOF MATERIAL" value={scope?.Material} />
                            <GridCell label="ROOF COLOR" value={scope?.Color} />
                            <GridCell label="ROOF PITCH" value={scope?.Pitch} multiline />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="TEAR-OFF LAYERS" value={scope?.Layers} />
                            <GridCell label="ROOF SQUARES" value={scope?.Squares} />
                            <GridCell label="MFR. WARRANTY" value={scope?.Warranty} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="UNDERLAYMENT" value={scope?.Underlayment} />
                            <GridCell label="ICE & WATER SHIELD" value={scope?.IceWaterShield} />
                            <GridCell label="STARTER STRIP" value={scope?.StarterStrip} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="EAVE DRIP EDGE" value={scope?.EaveDripEdge} />
                            <GridCell label="RAKE DRIP EDGE" value={scope?.RakeDripEdge} />
                            <GridCell label="VALLEY TYPE" value={scope?.ValleyType} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="HIP & RIDGE CAP" value={scope?.HipRidgeCap} flex={1} />
                        </Stack>

                        <SectionHeader num="3" title="NAILING, VENTILATION & FLASHING" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="FIELD NAIL SIZE" value={vents?.FieldNailSize} />
                            <GridCell label="FIELD NAILS PER" value={vents?.FieldNailsPerShingle} />
                            <GridCell label="RIDGE NAIL SIZE" value={vents?.RidgeNailSize} />
                            <GridCell label="RIDGE NAILS PER" value={vents?.RidgeNailsPerCap} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="STEP FLASHING" value={vents?.StepFlashing} multiline />
                            <GridCell label="ROOF VENTILATION" value={vents?.RoofVentilation} multiline />
                            <GridCell label="PLUMBING VENTS" value={vents?.PlumbingVents} multiline />
                            <GridCell label="FURNACE VENT" value={vents?.FurnaceVent} multiline />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="RE-FLASH CHIMNEY" value={vents?.ReflashChimney} multiline />
                            <GridCell label="RE-FLASH SKYLIGHTS" value={vents?.ReflashSkylights} multiline />
                            <GridCell label="REPLACE SKYLIGHTS" value={vents?.ReplaceSkylights} multiline />
                        </Stack>

                        <SectionHeader num="4" title="SPECIAL PROJECT NOTES" />
                        <Box sx={{ p: 1.5, border: "0.2px solid", borderColor: "neutral.200", minHeight: 60 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "neutral.800", whiteSpace: "pre-line" }}>
                                {localData?.Notes || "No special notes for this project."}
                            </Typography>
                        </Box>

                        <SectionHeader num="5" title="GUTTER SCOPE" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="REMOVE EXISTING GUTTERS" value={gutters?.RemoveExisting} />
                            <GridCell label="REMOVE EXISTING DOWNSPOUTS" value={gutters?.RemoveDownspouts} />
                            <GridCell label="INSTALL NEW GUTTERS" value={gutters?.InstallNew} />
                            <GridCell label="INSTALL NEW DOWNSPOUTS" value={gutters?.InstallDownspouts} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="GUTTER LENGTH" value={gutters?.GutterLength} />
                            <GridCell label="DOWNSPOUT LENGTH" value={gutters?.DownspoutLength} />
                            <GridCell label="GUTTER SIZE" value={gutters?.GutterSize} />
                            <GridCell label="GUTTER COLOR" value={gutters?.GutterColor} />
                        </Stack>
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="DOWNSPOUT SIZE" value={gutters?.DownspoutSize} />
                            <GridCell label="DOWNSPOUT COLOR" value={gutters?.DownspoutColor} />
                        </Stack>

                        <SectionHeader num="6" title="ADDITIONAL INFORMATION" />
                        <Box sx={{ p: 1.5, border: "0.2px solid", borderColor: "neutral.200", bgcolor: "#F7FFFA" }}>
                            <Stack spacing={0.5}>
                                {ADDITIONAL_INFO_ITEMS.map((item) => (
                                    <Typography key={item} sx={{ fontSize: "10px", color: "neutral.700" }}>
                                        ✓ {item}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>

                        <SectionHeader num="7" title="PRICING & PAYMENT" />
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="ROOF PRICE" value={pricing?.RoofPrice} />
                            <GridCell label="GUTTER PRICE" value={pricing?.GutterPrice} />
                            <GridCell label="TOTAL PRICE" value={pricing?.TotalPrice} />
                        </Stack>
                        {pricing?.Breakdown?.length ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    border: "0.2px solid",
                                    borderColor: "neutral.200",
                                    bgcolor: "#FAFBFC",
                                    mt: 2,
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "8px",
                                        fontWeight: 700,
                                        color: "neutral.500",
                                        textTransform: "uppercase",
                                        px: 1,
                                        pt: 1,
                                        pb: 2,
                                        borderBottom: "0.2px solid",
                                        borderColor: "neutral.200",
                                    }}
                                >
                                    Price Breakdown
                                </Typography>
                                <Box sx={{ py: 0.5 }}>
                                {pricing.Breakdown.map((line: { label: string; amount: string; isTotal?: boolean }) => (
                                    <Box
                                        key={line.label}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            px: 1,
                                            py: 0.5,
                                            borderBottom: line.isTotal ? "none" : "0.2px solid",
                                            borderColor: "neutral.100",
                                            bgcolor: line.isTotal ? "#EEF2F7" : "transparent",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: line.isTotal ? "10px" : "9px",
                                                fontWeight: line.isTotal ? 700 : 500,
                                                color: "neutral.800",
                                            }}
                                        >
                                            {line.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: line.isTotal ? "11px" : "9px",
                                                fontWeight: 700,
                                                color: line.isTotal ? "#1D3557" : "neutral.800",
                                            }}
                                        >
                                            {line.amount}
                                        </Typography>
                                    </Box>
                                ))}
                                </Box>
                            </Box>
                        ) : null}
                        <Stack direction="row" sx={{ width: "100%" }}>
                            <GridCell label="PAYMENT SCHEDULE" value={pricing?.PaymentSchedule} multiline flex={2} />
                            <GridCell label="ESTIMATED START DATE" value={pricing?.EstimatedStartDate} />
                        </Stack>
                        {pricing?.UpgradeAmount ? (
                            <Stack direction="row" sx={{ width: "100%" }}>
                                <GridCell
                                    label="OPTIONAL SHINGLE UPGRADE"
                                    value={`${pricing.UpgradeNotes || "Upgrade"} — ${pricing.UpgradeAmount}`}
                                    multiline
                                    flex={1}
                                />
                            </Stack>
                        ) : null}

                        <SectionHeader num="8" title="SPECIAL CONDITIONS FOR INSURANCE CLAIMS" />
                        <Box sx={{ p: 1.5, bgcolor: COLOR_INSURANCE, border: "0.5px solid #E6D5A1" }}>
                            <Typography sx={{ fontSize: "9px", lineHeight: 1.4, color: "neutral.800" }}>
                                This agreement does not obligate the homeowners or 1st Colorado Roofing in any way
                                unless payment for damage is approved by both the homeowner’s insurance company as well
                                as 1st Colorado Roofing. By signing this agreement, the homeowner authorizes 1st
                                Colorado Roofing to pursue the homeowner&apos;s best interest for restoration of damage,
                                at the agreed upon price between the insurance company and 1st Colorado Roofing. There
                                will be no additional cost to the homeowner except for the deductible.
                            </Typography>
                        </Box>

                        <SectionHeader num="9" title="ACCEPTANCE & SIGNATURES" />
                        <Stack direction="row" sx={{ width: "100%", mt: 1, gap: 4 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 3, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>OWNER SIGNATURE</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ borderBottom: "1px solid", borderColor: "neutral.400", mt: 3, mb: 1 }} />
                                <Typography sx={{ fontSize: "8px", fontWeight: 700 }}>
                                    1ST COLORADO ROOFING REPRESENTATIVE
                                </Typography>
                            </Box>
                        </Stack>

                        <Box
                            sx={{
                                mt: 4,
                                textAlign: "center",
                                borderTop: "2px solid",
                                borderColor: COLOR_HEADER,
                                pt: 1.5,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: "9px", fontWeight: 800, color: COLOR_HEADER, letterSpacing: "2px" }}
                            >
                                WWW.1STCOLORADOROOFING.COM
                            </Typography>
                        </Box>
                    </Sheet>
                </Box>

                <Box
                    sx={{
                        p: 3,
                        pt: 2,
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        mt: "auto",
                        position: "sticky",
                        bottom: 0,
                        bgcolor: "background.level1",
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {isSendDisabled ? (
                        <Button
                            variant="solid"
                            color="primary"
                            startDecorator={<FileDown size={20} />}
                            loading={isGenerating}
                            onClick={() => onGenerate?.(localData)}
                            sx={{ px: 4 }}
                        >
                            Generate Contract
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="neutral"
                                startDecorator={<RotateCw size={20} />}
                                loading={isGenerating}
                                onClick={() => onGenerate?.(localData)}
                                sx={{ px: 4 }}
                            >
                                Regenerate Contract
                            </Button>
                            <Button
                                variant="solid"
                                color="success"
                                startDecorator={<Send size={20} />}
                                loading={isSending}
                                onClick={onSend}
                                sx={{ px: 4 }}
                            >
                                Send to BoldSign
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </SideModal>
    );
}
